import React from 'react';
import { CheckIcon } from '../../SvgIconsV3/Check';

import { ProgressSection, ProgressStep, ProgressIndex, Divider } from './OnboardProgressBar.styles';
import { useOnboardingContext } from '../context';

export type OnboardProgressBarProps = {
  className?: string;
  allSections: (React.ReactChild | React.ReactFragment | React.ReactPortal)[];
  sectionIdx: number;
  progressTitles?: string[];
  progressOverrides?: number[];
};

const OnboardProgressBar = ({
  className,
  allSections,
  progressTitles,
  sectionIdx,
  progressOverrides,
}: OnboardProgressBarProps): JSX.Element => {
  const { onboardingState } = useOnboardingContext();
  const { absoluteStepIndex } = onboardingState;

  const renderItem = ({
    progressTitle,
    isActive,
    isComplete,
    notLast,
    index,
  }: {
    progressTitle: string;
    isActive: boolean;
    isComplete: boolean;
    notLast: boolean;
    index: number;
  }) => (
    <React.Fragment key={`${progressTitle}=${index}`}>
      <ProgressStep isActive={isActive}>
        <ProgressIndex isActive={isActive} isComplete={isComplete}>
          {isComplete ? <CheckIcon /> : index + 1}
        </ProgressIndex>
        {progressTitle}
      </ProgressStep>
      {notLast && <Divider />}
    </React.Fragment>
  );

  if (progressOverrides && progressTitles?.length === progressOverrides.length) {
    return (
      <ProgressSection data-testid="OnboardProgressBar" className={className}>
        {progressTitles?.map((progressTitle, i) => {
          const titleStartIndex = progressOverrides[i];
          const titleEndIndex = progressOverrides?.[i + 1];
          const notLast = titleEndIndex !== undefined;
          const isActive = notLast
            ? Boolean(titleStartIndex <= absoluteStepIndex && absoluteStepIndex < titleEndIndex)
            : Boolean(titleStartIndex <= absoluteStepIndex);
          const isComplete = absoluteStepIndex >= titleEndIndex;
          return renderItem({ progressTitle, isActive, isComplete, notLast, index: i });
        })}
      </ProgressSection>
    );
  }

  return (
    <ProgressSection data-testid="OnboardProgressBar" className={className}>
      {allSections.map((_, i) => {
        const isActive = sectionIdx === i;
        const isComplete = i < sectionIdx;
        const notLast = i < allSections.length - 1;
        const progressTitle = progressTitles?.[i] as string;
        return renderItem({ progressTitle, isActive, isComplete, notLast, index: i });
      })}
    </ProgressSection>
  );
};
export default OnboardProgressBar;
